import { toast } from "@indigo-components";
import { useCallback, useEffect, useState } from "react";
import {
  fromE164,
  getUserSecurity,
  isCompletePhone,
  UserSecuritySettings,
  updateUserSecurity,
} from "~/services/userSecurity";

/**
 * The signed-in user's auth phone + SMS 2FA, for the User Settings modal.
 * Loads each time the modal opens and holds edits locally until `save`
 * (called on Save). Admins editing other users go through
 * SecuritySettingsSection instead.
 */
export const useOwnSecuritySettings = (
  cognitoSub: string | undefined,
  isOpen: boolean,
) => {
  const [settings, setSettings] = useState<UserSecuritySettings | null>(null);
  const [phoneDigits, setPhoneDigits] = useState("");
  const [smsMfaEnabled, setSmsMfaEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const applySettings = useCallback((result: UserSecuritySettings) => {
    setSettings(result);
    setPhoneDigits(fromE164(result.phoneNumber));
    setSmsMfaEnabled(result.smsMfaEnabled);
  }, []);

  useEffect(() => {
    // Drop edits on close so a reopen never flashes discarded values before
    // the reload lands.
    if (!isOpen) {
      setSettings(null);
      setPhoneDigits("");
      setSmsMfaEnabled(false);
      return;
    }
    if (!cognitoSub) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    getUserSecurity(cognitoSub)
      .then((result) => {
        if (!cancelled) applySettings(result);
      })
      .catch((error: unknown) => {
        console.error("Failed to load 2FA settings:", error);
        if (!cancelled) {
          setLoadError(
            error instanceof Error ? error.message : "Failed to load",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, cognitoSub, applySettings]);

  const isDirty =
    !!settings &&
    (phoneDigits !== fromE164(settings.phoneNumber) ||
      smsMfaEnabled !== settings.smsMfaEnabled);

  /** Writes pending edits. Resolves false (after toasting why) on failure. */
  const save = useCallback(async (): Promise<boolean> => {
    if (!cognitoSub || !isDirty) return true;
    if (smsMfaEnabled && !isCompletePhone(phoneDigits)) {
      toast.error("Set a phone number before enabling SMS 2FA");
      return false;
    }
    setIsSaving(true);
    try {
      applySettings(
        await updateUserSecurity(cognitoSub, phoneDigits, smsMfaEnabled),
      );
      toast.success("2FA settings updated");
      return true;
    } catch (error) {
      console.error("Failed to save 2FA settings:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to save changes",
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [cognitoSub, isDirty, smsMfaEnabled, phoneDigits, applySettings]);

  return {
    settings,
    phoneDigits,
    setPhoneDigits,
    smsMfaEnabled,
    setSmsMfaEnabled,
    canEnableMfa: isCompletePhone(phoneDigits),
    isDirty,
    // Also true before the first load lands, so controls start disabled.
    isLoading: isLoading || (isOpen && !settings && !loadError),
    isSaving,
    loadError,
    save,
  };
};
